import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useHomepage, useCategories, useSettings } from '@/hooks/useProducts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { IS_SAAS, IS_RUMIO, IS_LODGING, IS_SINGLE_PROPERTY, SITE_TYPE } from '@/config/site';
import { resolveLook } from '@/looks';
import SaasHome from './saas/SaasHome';
import DocumentHead from '@/components/DocumentHead';
import QuickEditWrapper from '@/components/quickedit/QuickEditWrapper';
import ReviewCarousel from '@/components/ReviewCarousel';
import { LodgingBusinessJsonLd } from '@/components/property/SchemaOrgJsonLd';

// Homepage section components
import HeroSection from '@/components/homepage/HeroSection';
import FeaturedProducts from '@/components/homepage/FeaturedProducts';
import AppEmbedSection from '@/components/homepage/AppEmbedSection';
import CategoryGrid from '@/components/homepage/CategoryGrid';
import StorySection from '@/components/homepage/StorySection';
import ImageBanner from '@/components/homepage/ImageBanner';
import SeasonalSpotlight from '@/components/homepage/SeasonalSpotlight';
import ValuePropositions from '@/components/homepage/ValuePropositions';
import ImageGallery from '@/components/homepage/ImageGallery';
import Newsletter from '@/components/homepage/Newsletter';
import Testimonials from '@/components/homepage/Testimonials';
import Banner from '@/components/homepage/Banner';
import GrowingTips from '@/components/homepage/GrowingTips';
import CustomHtml from '@/components/homepage/CustomHtml';
import PricingTiers from '@/components/homepage/PricingTiers';
import CourseHighlight from '@/components/homepage/CourseHighlight';
import InstructorBios from '@/components/homepage/InstructorBios';
import FAQ from '@/components/homepage/FAQ';
import StatsCounter from '@/components/homepage/StatsCounter';
import TrustBadges from '@/components/homepage/TrustBadges';
import LearningPath from '@/components/homepage/LearningPath';
import VideoShowcase from '@/components/homepage/VideoShowcase';
import FeatureComparison from '@/components/homepage/FeatureComparison';
import CTABanner from '@/components/homepage/CTABanner';
import ProcessSteps from '@/components/homepage/ProcessSteps';
import SectionControls from '@/components/quickedit/SectionControls';
import {
  CurrentExhibitHero,
  VisitCard,
  EventsTeaser,
  MissionBrief,
  DonateCta,
  MembershipTiersSection,
} from '@/components/homepage/MuseumSections';
import {
  AvailabilitySearchHero,
  FeaturedProperties,
  HostIntro,
  AreaHighlights,
  ReviewsTeaser,
  DirectBookPitch,
} from '@/components/homepage/LodgingSections';
import {
  PlayNowHero,
  HowItWorksSteps,
  FeaturedGamesGrid,
  WinnersTeaser,
  ResponsiblePlayBadge,
  AppEmbedTeaser,
} from '@/components/homepage/SocialCasinoSections';
// Lazy so the single-property home (and its PropertyDetail + Mapbox deps) is a
// separate chunk — non-single-property tenants never load it. Lazy also makes
// SinglePropertyHome's static `import { SectionRenderer } from './Home'` safe
// (this edge is dynamic, so there's no static import cycle).
const SinglePropertyHome = lazy(() => import('./SinglePropertyHome'));

function SectionContent({ section }: { section: any }) {
  switch (section.section_type) {
    case 'hero':
      return <HeroSection section={section} />;
    case 'featured_products':
    case 'new_arrivals':
      return <FeaturedProducts section={section} />;
    case 'categories':
      return <CategoryGrid section={section} />;
    case 'story':
      return <StorySection section={section} />;
    case 'image_banner':
      return <ImageBanner section={section} />;
    case 'seasonal_spotlight':
      return <SeasonalSpotlight section={section} />;
    case 'value_props':
      return <ValuePropositions section={section} />;
    case 'image_gallery':
      return <ImageGallery section={section} />;
    case 'newsletter':
      return <Newsletter section={section} />;
    case 'testimonials':
      return <Testimonials section={section} />;
    case 'banner':
      return <Banner section={section} />;
    case 'growing_tips':
      return <GrowingTips section={section} />;
    case 'custom_html':
      return <CustomHtml section={section} />;
    case 'pricing_tiers':
      return <PricingTiers key={section.id} section={section} />;
    case 'course_highlight':
      return <CourseHighlight key={section.id} section={section} />;
    case 'instructor_bios':
      return <InstructorBios key={section.id} section={section} />;
    case 'faq':
      return <FAQ key={section.id} section={section} />;
    case 'stats_counter':
      return <StatsCounter key={section.id} section={section} />;
    case 'trust_badges':
      return <TrustBadges key={section.id} section={section} />;
    case 'learning_path':
      return <LearningPath key={section.id} section={section} />;
    case 'video_showcase':
      return <VideoShowcase key={section.id} section={section} />;
    case 'feature_comparison':
      return <FeatureComparison key={section.id} section={section} />;
    case 'cta_banner':
      return <CTABanner key={section.id} section={section} />;
    case 'process_steps':
      return <ProcessSteps key={section.id} section={section} />;
    case 'app_embed':
      return <AppEmbedSection key={section.id} section={section} />;
    // Museum-flavored sections (live for siteType='museum'; other tenants
    // can also opt-in via the homepage builder if they want a museum-shaped
    // hero or visit card).
    case 'current_exhibit_hero':
      return <CurrentExhibitHero key={section.id} section={section} />;
    case 'visit_card':
      return <VisitCard key={section.id} section={section} />;
    case 'events_teaser':
      return <EventsTeaser key={section.id} section={section} />;
    case 'mission_brief':
      return <MissionBrief key={section.id} section={section} />;
    case 'donate_cta':
      return <DonateCta key={section.id} section={section} />;
    case 'membership_tiers':
      return <MembershipTiersSection key={section.id} section={section} />;
    // Lodging-flavored sections (live for siteType='lodging'; other tenants
    // can opt-in via the homepage builder if they want a property-shaped
    // hero or featured-stays grid).
    case 'availability_search':
      return <AvailabilitySearchHero key={section.id} section={section} />;
    case 'featured_properties':
      return <FeaturedProperties key={section.id} section={section} />;
    case 'host_intro':
      return <HostIntro key={section.id} section={section} />;
    case 'area_highlights':
      return <AreaHighlights key={section.id} section={section} />;
    case 'reviews_teaser':
      return <ReviewsTeaser key={section.id} section={section} />;
    case 'direct_book_pitch':
      return <DirectBookPitch key={section.id} section={section} />;
    // Social-casino-flavored sections (live for siteType='social_casino';
    // other tenants can opt-in via the homepage builder).
    case 'play_now_hero':
      return <PlayNowHero key={section.id} section={section} />;
    case 'how_it_works_steps':
      return <HowItWorksSteps key={section.id} section={section} />;
    case 'featured_games_grid':
      return <FeaturedGamesGrid key={section.id} section={section} />;
    case 'winners_teaser':
      return <WinnersTeaser key={section.id} section={section} />;
    case 'responsible_play_badge':
      return <ResponsiblePlayBadge key={section.id} section={section} />;
    case 'app_embed_teaser':
      return <AppEmbedTeaser key={section.id} section={section} />;
    default:
      return null;
  }
}

export function SectionRenderer({
  section,
  siblings,
}: {
  section: any;
  /**
   * The other sections on the page, in display order. Needed because a reorder
   * is one atomic PUT of the whole list — moving a section by rewriting only
   * its own display_order would collide with whatever already held that slot.
   *
   * Omitted by callers that render a section outside the homepage list; the
   * move controls then don't appear, which is correct.
   */
  siblings?: { id: string; display_order?: number }[];
}) {
  const content = <SectionContent section={section} />;
  if (!content || !section.id || String(section.id).startsWith('default-')) {
    return content;
  }

  // Only real, persisted sections in a known order can be moved. Defaults (the
  // seeded fallbacks with `default-` ids) have no row to update.
  const ordered = (siblings ?? []).filter(
    (s) => s.id && !String(s.id).startsWith('default-'),
  );
  const index = ordered.findIndex((s) => s.id === section.id);
  const movable = index >= 0 && ordered.length > 1;

  /**
   * Swap this section with its neighbour and return the whole list renumbered.
   *
   * Renumbering all of them rather than swapping two display_order values keeps
   * the sequence dense and total, so a page whose orders have drifted (repeated
   * values, gaps from deletions — both exist in production data) still moves
   * predictably instead of appearing not to move at all.
   */
  const moveTo = (direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    const next = [...ordered];
    [next[index], next[target]] = [next[target], next[index]];
    return next.map((s, i) => ({ id: s.id, display_order: i }));
  };

  const label = section.title || String(section.section_type).replace(/_/g, ' ');

  return (
    <QuickEditWrapper
      href="/admin/homepage"
      label={`Edit section: ${label}`}
      controls={
        movable ? (
          <SectionControls
            sectionId={section.id}
            isActive={section.is_active !== false}
            canMoveUp={index > 0}
            canMoveDown={index < ordered.length - 1}
            onMove={moveTo}
            label={label}
          />
        ) : undefined
      }
    >
      {content}
    </QuickEditWrapper>
  );
}

function HomepageSkeleton() {
  const { t } = useTranslation('home');
  return (
    <div aria-busy="true" aria-label={t('skeleton.ariaLabel')}>
      {/* Hero skeleton */}
      <div className="h-[60vh] bg-sage-100 animate-pulse" />
      {/* Products skeleton */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="h-8 w-48 bg-sage-100 rounded animate-pulse mx-auto mb-8" />
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse" aria-hidden="true">
              <div className="aspect-[3/4] rounded-lg bg-sage-100" />
              <div className="mt-3 h-4 rounded bg-sage-100 w-3/4" />
              <div className="mt-2 h-4 rounded bg-sage-100 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Fallback hero when no CMS sections exist
function DefaultHero() {
  const { t } = useTranslation('home');
  const { isEnabled } = useFeatureFlags();
  const showEcommerce = isEnabled('ecommerce');
  const showCourses = isEnabled('courses');

  const ctaLink = showEcommerce ? '/shop' : showCourses ? '/courses' : '/';
  const subtitle = showEcommerce
    ? t('hero.subtitleEcommerce')
    : showCourses
      ? t('hero.subtitleCourses')
      : t('hero.subtitleGeneric');

  return (
    <HeroSection
      section={{
        id: 'default-hero',
        section_type: 'hero',
        title: t('hero.defaultTitle'),
        subtitle,
        content: {
          cta_text: t('hero.defaultCta'),
          cta_link: ctaLink,
        },
      }}
    />
  );
}

function DefaultCategories() {
  const { t } = useTranslation('home');
  const { data, isLoading } = useCategories();
  const categories: any[] = data?.categories ?? [];

  if (isLoading || categories.length === 0) return null;

  return (
    <CategoryGrid
      section={{
        id: 'default-categories',
        section_type: 'categories',
        title: t('categories.browseHeading'),
        subtitle: t('categories.browseSubtitle'),
        categories,
      }}
    />
  );
}

export default function Home() {
  // rumio.world renders its hardcoded SaasHome (platform-selling-itself copy).
  // Every other SaaS tenant falls through to the normal CMS-driven homepage,
  // which reads from /api/homepage and renders the wizard-seeded sections
  // (hero, pricing_tiers, stats_counter, testimonials, faq, cta_banner —
  // all tenant-specific).
  if (IS_SAAS && IS_RUMIO) {
    return <SaasHome />;
  }

  // Single-property lodging sites render the property itself as the homepage
  // (photo hero + booking widget) with the marketing sections below.
  if (IS_LODGING && IS_SINGLE_PROPERTY) {
    return (
      <Suspense fallback={null}>
        <SinglePropertyHome />
      </Suspense>
    );
  }

  const { t } = useTranslation('home');
  const { data, isLoading } = useHomepage();
  const { data: settingsData } = useSettings();
  const { isEnabled } = useFeatureFlags();
  const showEcommerce = isEnabled('ecommerce');
  const showCourses = isEnabled('courses');
  const allSections: any[] = data?.sections ?? [];

  // Lodging hero dedupe: when the tenant's Look renders the `search_forward`
  // hero (a photo hero that already carries the availability search bar), a
  // seeded `availability_search` section would stack a second near-identical
  // search hero directly beneath it. Keep the Look hero; drop the duplicate.
  const heroVariant = resolveLook(settingsData?.settings?.theme_config?.look, SITE_TYPE).hero_variant;
  const sections =
    IS_LODGING &&
    heroVariant === 'search_forward' &&
    allSections.some((s) => s.section_type === 'hero')
      ? allSections.filter((s) => s.section_type !== 'availability_search')
      : allSections;

  const metaDescription = IS_LODGING
    ? t('meta.descriptionLodging')
    : showEcommerce
      ? t('meta.descriptionEcommerce')
      : showCourses
        ? t('meta.descriptionCourses')
        : t('meta.descriptionGeneric');

  const newsletterSubtitle = showEcommerce
    ? t('newsletter.subtitleEcommerce')
    : t('newsletter.subtitleGeneric');

  if (isLoading) {
    return <HomepageSkeleton />;
  }

  // If no sections from CMS, show defaults
  if (sections.length === 0) {
    if (IS_LODGING) {
      return (
        <>
          <DocumentHead
            title={t('meta.titleHome')}
            description={metaDescription}
            canonicalUrl="/"
          />
          <LodgingBusinessJsonLd description={metaDescription} />
          <AvailabilitySearchHero
            section={{
              id: 'default-availability-search',
              title: t('hero.lodgingTitle'),
              subtitle: t('meta.descriptionLodging'),
              content: { cta_href: '/properties', cta_label: t('hero.lodgingCta') },
            }}
          />
          <Newsletter
            section={{
              id: 'default-newsletter',
              section_type: 'newsletter',
              title: t('newsletter.defaultTitle'),
              subtitle: newsletterSubtitle,
            }}
          />
        </>
      );
    }
    return (
      <>
        <DocumentHead
          title={t('meta.titleHome')}
          description={metaDescription}
          canonicalUrl="/"
        />
        <DefaultHero />
        {showEcommerce && <DefaultCategories />}
        <Newsletter
          section={{
            id: 'default-newsletter',
            section_type: 'newsletter',
            title: t('newsletter.defaultTitle'),
            subtitle: newsletterSubtitle,
          }}
        />
      </>
    );
  }

  return (
    <>
      <DocumentHead
        title={t('meta.titleHome')}
        description={metaDescription}
        canonicalUrl="/"
      />
      {IS_LODGING && <LodgingBusinessJsonLd description={metaDescription} />}
      {sections.map((section: any) => (
        <SectionRenderer key={section.id} section={section} siblings={sections} />
      ))}
      {/* ReviewCarousel pulls product reviews — skip it on lodging (no products;
          lodging reviews render per-property + via the reviews_teaser section). */}
      {!IS_LODGING && <ReviewCarousel />}
    </>
  );
}
